import { createPageIcon } from './create-page-icon'

function setAttributes (element, attributes) {
  if (attributes) {
    Object.keys(attributes).forEach(k => {
      element.setAttribute(k, attributes[k])
    })
  }
}

function getCacheBuster (cache = true) {
  return cache ? '' : `?${Date.now()}`
}

function createScript (url, attributes = null, cache = true) {
  const script = document.createElement('script')
  script.setAttribute('type', 'text/javascript')
  script.setAttribute('src', `${url}${getCacheBuster(cache)}`)
  setAttributes(script, attributes)
  return script
}

function createStyle (url, attributes = null, cache = true) {
  const style = document.createElement('link')
  style.setAttribute('rel', 'stylesheet')
  style.setAttribute('type', 'text/css')
  style.setAttribute('href', `${url}${getCacheBuster(cache)}`)
  setAttributes(style, attributes)
  return style
}

function createPromise (item) {
  let dep = item
  if (Array.isArray(item) && item.length === 2 && item[0].test) {
    if (item[0].test()) {
      dep = item[0]
      if (dep.is === 'script' && /\.esm\.js/.test(dep.url)) dep.attributes = { type: 'module' }
    } else {
      dep = item[1]
    }
  }

  if (!dep.url || !dep.is) {
    return Promise.reject(new Error('No url or type given!'))
  }

  if (!window.mosaic.components.libraries[dep.url]) {
    const promise = new Promise((resolve, reject) => {
      const handleResponse = (e) => {
        if (e.error || (e.type && e.type === 'error')) {
          reject(e.error)
        } else {
          window.mosaic.components.libraries[dep.url].hasLoaded = true
          resolve()
        }
      }

      if (dep.is === 'script') {
        const script = createScript(dep.url, dep.attributes, dep.cache)
        script.onerror = handleResponse
        script.onload = handleResponse
        document.body.appendChild(script)
      } else if (dep.is === 'style') {
        const style = createStyle(dep.url, dep.attributes, dep.cache)
        if (dep.fallback) {
          const fallbackStyle = createStyle(dep.fallback, dep.attributes, dep.cache)
          const handleDefaultResponse = (e) => {
            if (e.error || (e.type && e.type === 'error')) {
              document.getElementsByTagName('head')[0].appendChild(fallbackStyle)
            } else {
              window.mosaic.components.libraries[dep.url].hasLoaded = true
              resolve()
            }
          }
          style.onerror = handleDefaultResponse
          style.onload = handleDefaultResponse
          fallbackStyle.onerror = handleResponse
          fallbackStyle.onload = handleResponse
          document.getElementsByTagName('head')[0].appendChild(style)
        } else {
          style.onerror = handleResponse
          style.onload = handleResponse
          document.getElementsByTagName('head')[0].appendChild(style)
        }
      } else if (dep.is === 'svg') {
        window.fetch(dep.url)
          .then((resp) => resp.text())
          .then(function (data) {
            const viewBoxRegex = /<svg.*?viewBox="([^"]+)".*?>/im
            const svgContentsRegex = /<svg.*?>([\s\S]+?)<\/svg>/m
            const viewBoxMatches = data.match(viewBoxRegex)
            const contentMatches = data.match(svgContentsRegex)
            if (contentMatches && contentMatches.length === 2) {
              const pv = contentMatches[1]
              let vb = '0 0 32 32'
              if (viewBoxMatches.length === 2) {
                vb = viewBoxMatches[1]
              }
              createPageIcon(dep.url, pv, vb)
              resolve()
            } else {
              reject(new Error(`Unable to load SVG contents for icon: ${dep.url}`))
            }
          })
      }
    })
    window.mosaic.components.libraries[dep.url] = { type: dep.is, hasLoaded: false, promise }
    return promise
  } else {
    return window.mosaic.components.libraries[dep.url].promise
  }
}

/**
 * Function to load one or more external resources
 * @param {...object|Array} items The resources to load
 * @returns {Promise} A pending promise
 * @example
 * loadExternal(
 *   { url: getComponentPath('/css/label.css'), is: 'style' },
 *   [
 *     { url: getComponentPath('/js/label.esm.js'), is: 'script', test: esmTest },
 *     { url: getComponentPath('/js/label.iife.js'), is: 'script' }
 *   ]
 * )
 */
export function loadExternal (...items) {
  window.mosaic = window.mosaic || {}
  window.mosaic.components = window.mosaic.components || {}
  window.mosaic.components.libraries = window.mosaic.components.libraries || {}
  if (!items.length) return Promise.resolve()
  const promises = items.map(x => {
    return createPromise(x)
  })
  return Promise.all(promises)
}

export function loadScriptCallback (item, callback) {
  window.mosaic = window.mosaic || {}
  window.mosaic.components = window.mosaic.components || {}
  window.mosaic.components.libraries = window.mosaic.components.libraries || {}
  if (!item.url || !item.is) {
    return callback(new Error('No url or type given!'))
  }
  if (!window.mosaic.components.libraries[item.url]) {
    const handleResponse = (e) => {
      if (e.error) {
        callback(e.error)
      } else {
        window.mosaic.components.libraries[item.url].hasLoaded = true
        callback(null)
      }
    }

    if (item.is === 'script') {
      const script = createScript(item.url, item.attributes, item.cache)
      script.onerror = handleResponse
      script.onload = handleResponse
      document.body.appendChild(script)
    } else if (item.is === 'style') {
      const style = createStyle(item.url, item.attributes, item.cache)
      style.onerror = handleResponse
      style.onload = handleResponse
      document.getElementsByTagName('head')[0].appendChild(style)
    }

    window.mosaic.components.libraries[item.url] = { type: item.is, hasLoaded: false, promise: null }
  } else {
    callback(null)
  }
}
